import type { ReactNode } from "react";

import { InfoTip } from "@pervasivemind/bi-components";

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  /**
   * Pre-resolved tooltip text (config ``info``, already localized by
   * the caller). Renders an ``InfoTip`` ⓘ next to the title — the
   * plain-language explanation Arturo asked for ("alguien que no sepa
   * nada del negocio lo tiene que entender", 2026-07-16).
   */
  info?: string;
  rightContent?: ReactNode;
  /**
   * Skeleton mode (`DashboardSkeleton`): render pulse bars in place of
   * the title/subtitle text, INSIDE the exact same container box
   * (px-5 py-3, accent pip included) with bar heights matching the text
   * line heights (h2 text-base → 24px, subtitle text-sm → 20px + mt-0.5) —
   * so the loading header occupies precisely the space the real header
   * will (uniform-grid review #7b). A truthy `subtitle` reserves the
   * subtitle line. In `compact` mode the skeleton mirrors the single
   * text line (one pulse bar in the same px-5 py-2 box).
   */
  skeleton?: boolean;
  /**
   * Compact single-line variant (table cards — UI polish 2026-07).
   * Title and subtitle share ONE baseline-aligned line (subtitle as a
   * secondary inline datum, truncating) with tighter padding
   * (px-5 py-2 vs px-5 py-3) — saves ~28px per table card. Dashboard
   * SECTION headers keep the default two-line layout, and are the only
   * variant that is LIGHT: see the comment on that branch.
   */
  compact?: boolean;
}

export function SectionHeader({
  title,
  subtitle,
  info,
  rightContent,
  skeleton = false,
  compact = false,
}: SectionHeaderProps): JSX.Element {
  if (compact) {
    return (
      // Look v3 (2026-10-24): the table card header is part of the white
      // card, a line under it, light controls on the right.
      <div className="flex items-center justify-between gap-3 rounded-t-[12px] border-b border-neutral-200 bg-white px-4 py-3">
        {skeleton ? (
          <div className="h-5 w-48 animate-pulse rounded bg-neutral-200" />
        ) : (
          <div className="flex min-w-0 items-baseline gap-2">
            {/* Title truncates (min-w-0) instead of painting over the
                rightContent controls when the card gets narrow. */}
            <h2 className="min-w-0 truncate text-[13px] font-semibold text-neutral-800">
              {title}
            </h2>
            {info ? <InfoTip text={info} className="self-center text-neutral-400" /> : null}
            {subtitle ? (
              <span className="min-w-0 truncate text-2xs text-neutral-500 tabular-nums">
                {subtitle}
              </span>
            ) : null}
          </div>
        )}
        {rightContent ? (
          <div className="flex shrink-0 items-center gap-3">{rightContent}</div>
        ) : null}
      </div>
    );
  }
  // Dashboard SECTION band — plain (nuevo look Omnitok, iteración 2,
  // 2026-10-24). A section is the grouping around the objects that ARE
  // the report, so it carries no card and no decoration: the title in
  // the display face, the subtitle muted under it, the ⓘ beside the
  // title. The accent pip went with the v2 review (no side tabs: the
  // colour belongs to the data, not to the frame).
  //
  // The TABLE-CARD variant above is the white header of a card (look v3,
  // 2026-10-24; it was a navy band until then). RowDrillPanel's swap
  // header mirrors it so the card keeps its anatomy across the swap.
  return (
    <div data-section-header="" className="flex items-start justify-between gap-3 pb-2.5">
      <div className="min-w-0">
        {skeleton ? (
          <div className="h-6 w-40 animate-pulse rounded bg-neutral-200" />
        ) : (
          <div className="flex items-center gap-1.5">
            <h2 className="font-display text-[15px] font-extrabold leading-6 text-neutral-900">
              {title}
            </h2>
            {info ? <InfoTip text={info} className="text-neutral-400" /> : null}
          </div>
        )}
        {subtitle ? (
          skeleton ? (
            <div className="mt-0.5 h-[18px] w-56 animate-pulse rounded bg-neutral-200" />
          ) : (
            <p className="mt-0.5 text-[11.5px] leading-[18px] text-neutral-500">{subtitle}</p>
          )
        ) : null}
      </div>
      {rightContent ? (
        <div className="flex shrink-0 items-center gap-3">{rightContent}</div>
      ) : null}
    </div>
  );
}
